import { animate, state, style, transition, trigger } from '@angular/animations';
import { NgClass } from '@angular/common';
import { Component, Input } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { MatLegacySelectModule } from '@angular/material/legacy-select';
import { MatLegacyTooltipModule } from '@angular/material/legacy-tooltip';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import dayjs from 'dayjs';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { filter, withLatestFrom } from 'rxjs/operators';
import { IdentityService } from 'src/app/core/identity.service';
import { FollowupStatusEnum } from 'src/app/recruiting/candidate-followups/models/followup-status.enum';
import { RecruitingService } from 'src/app/recruiting/recruiting.service';
import { CalendarComponent } from 'src/app/shared/components/calendar/calendar.component';
import { FollowupPickerProfileBaseComponent } from 'src/app/shared/components/followup-picker/followup-picker-profile-base.component';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import { FollowupView } from 'src/app/shared/models/followup-view.model';
import {
    selectConfirmationDialogData,
    selectConfirmationDialogResult
} from 'src/app/shared/selectors/confirmation-dialog.selectors';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { isEmpty } from 'src/app/shared/utilities/obj-utilities';

@UntilDestroy()
@Component({
    selector: 'nova-followup-picker',
    templateUrl: './followup-picker.component.html',
    styleUrls: ['./followup-picker.component.scss'],
    animations: [
        trigger('balloonEffect', [
            state(
                'initial',
                style({
                    opacity: 0
                })
            ),
            state(
                'final',
                style({
                    opacity: 1
                })
            ),
            transition('initial=>final', animate('1500ms')),
            transition('final=>initial', animate('1500ms'))
        ])
    ],
    standalone: true,
    providers: [provideNgxMask()],
    imports: [
        ReactiveFormsModule,
        MatLegacyFormFieldModule,
        MatLegacyTooltipModule,
        MatLegacySelectModule,
        MatLegacyInputModule,
        MatIconModule,
        MatLegacyButtonModule,
        CalendarComponent,
        NgClass,
        NgxMaskDirective
    ]
})
export class FollowupPickerComponent extends FollowupPickerProfileBaseComponent {
    @Input() currentFollowup: FollowupView;
    readonly featureFlag = FeatureFlag;
    // tracks followup details for display/edit for the current month
    followupDetails: FollowupView[] = [];

    constructor(
        private readonly _recruitingService: RecruitingService,
        private readonly _identityService: IdentityService,
        private readonly storeState: Store,
        protected readonly _featureManager: LDFeatureManager
    ) {
        super(_recruitingService, _identityService, storeState, _featureManager);
    }

    patchData(followUp: FollowupView): void {
        if (followUp && this.resourcesForm && followUp.status !== FollowupStatusEnum.Complete) {
            this.followUpId = followUp?.id;
            const priorIndex = this.priorityList.findIndex((e) => e.type === followUp.priority);
            const timeIndex = this.hours.findIndex((e) => e.id === followUp.followupTimeId);
            this.priority = this.priorityList[priorIndex];
            this.hour = this.hours[timeIndex];
            this.patchDataToForm(followUp, priorIndex, timeIndex);
        }
    }

    saveFollowUp(markComplete = false): void {
        if (!this.saveInProgress && !this.changeInProgress && this.resourcesForm?.value?.date) {
            this.saveInProgress = true;
            const follow = this.createDto(markComplete);
            this.updateRecruiterResources(this._recruitingService, follow, markComplete);
        }
    }

    isMarkedComplete(): boolean {
        return (
            isEmpty(this.currentFollowup) ||
            !this.currentFollowup?.followupDate ||
            this.currentFollowup?.status === FollowupStatusEnum.Complete
        );
    }

    handleMarkAsCompleteConfirmationResult(): void {
        this.storeState
            .select(selectConfirmationDialogResult)
            .pipe(
                filter((result) => result),
                withLatestFrom(this.storeState.select(selectConfirmationDialogData)),
                untilDestroyed(this)
            )
            .subscribe((confirmed) => {
                if (confirmed) {
                    this.markAsComplete();
                }
            });
    }

    protected initLookups(shouldSkipGetFollowupSummary: boolean) {
        this.getLookups(this.currentDate, this._recruitingService, shouldSkipGetFollowupSummary);
    }

    protected selectedDateChanged(newDate: Date): void {
        this.handleSelectedDateChanged(newDate, this._recruitingService);
    }

    protected createDto(markComplete = false): FollowupView {
        const dateWithTimeStr = `${this.resourcesForm.value.date} ${this.resourcesForm.value.time.followupTime}`;
        const date = dayjs(dateWithTimeStr).format('YYYY-MM-DD HH:mm');
        let followupStatus = FollowupStatusEnum.Incomplete;
        if (markComplete) {
            followupStatus = FollowupStatusEnum.Complete;
        }
        return {
            id: this.followUpId,
            priority: this.resourcesForm.value.priorityList.type,
            followupDate: date,
            recruiterId: this.recruiterId,
            candidateId: this.currentFollowup.candidateId,
            followupTimeId: this.resourcesForm.value.time.id,
            status: followupStatus,
            followupReason: this.resourcesForm.value.followupReason
        } as FollowupView;
    }

    protected markAsComplete(): void {
        this.saveFollowUp(true);
        this.resetDefaultValues();
        this.resourcesForm.markAsPristine();
        this.currentFollowup = {
            id: null,
            followupDate: null,
            followupTimeId: null,
            candidateId: this.currentFollowup.candidateId,
            recruiterId: this.currentFollowup.recruiterId,
            followupReason: null,
            priority: null,
            status: null
        };
        this.onChangeFollowup.emit([this.currentFollowup]);
    }
}
